import { useContext, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@reflex/ui/client/http';
import { useOrgKey } from '@reflex/ui/client/context';
import { usePluginEvent } from '@reflex/ui/client/socket';
import { useDebouncedValue } from '@reflex/ui/hooks/useDebouncedValue';
import { githubApi } from './api.js';
import { PullRequestBatchContext } from './pull-request-batch.js';
import { isNewerPullRequestDetails } from './pull-request-order.js';
import { GITHUB_PLUGIN_NAME } from '../shared/install-events.js';
import { GITHUB_PR_UPDATED_EVENT, GitHubPrUpdatedSchema } from '../shared/pr-events.js';
import { filterRepos, normalizeRepoQuery } from '../shared/repo-search.js';
import { rankRepos } from '../shared/repo-ranking.js';
import { useRepoSelectionHistory } from './useRepoSelectionHistory.js';
import type { GitHubRepoListResponse, GitHubBranch, GitHubRepo } from '@reflex/shared';

const REPO_STALE_TIME = 5 * 60_000;
const REPO_GC_TIME = 10 * 60_000;

/**
 * How many repos the one-shot list asks for. High enough that the response is
 * the caller's complete accessible set for any realistic account — which is
 * the point: a complete list is filtered in the browser, so typing costs zero
 * requests. Accounts past the cap come back `complete: false` and fall back to
 * server-side search.
 */
const REPO_LIST_LIMIT = 1000;

/** Debounce on the fallback server search only. Local filtering is applied to
 * the raw query, so typing stays instant whenever the list is complete. */
const REPO_SEARCH_DEBOUNCE_MS = 300;

/**
 * While the server is still paging the caller's repos out of GitHub it answers
 * from the first page and flags the response incomplete. Poll on that gap so
 * the list upgrades itself to the complete set within a few seconds — after
 * which searching never touches the network again.
 */
const INCOMPLETE_LIST_REFETCH_MS = 3_000;

export function isGitHubNotLinkedError(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'GITHUB_NOT_LINKED';
}

/**
 * Returns true when the API rejected the request because the caller has a
 * GitHub provider account row but no usable access token (e.g. the token
 * was never persisted, or migration `020_extract_provider_account_tokens`
 * ran against a row that lacked one). The user can fix this by clicking
 * the "Reconnect GitHub" button to refresh the credential.
 */
export function isGitHubTokenMissingError(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'GITHUB_TOKEN_MISSING';
}

/**
 * Returns true when the API rejected the request because of either an
 * unlinked account or a missing token — i.e. any state that the user can
 * resolve by going through `/auth/github`.
 */
export function isGitHubReconnectableError(error: unknown): boolean {
  return isGitHubNotLinkedError(error) || isGitHubTokenMissingError(error);
}

/** What a caller is missing before it can browse repos, and who can fix it. */
export interface GitHubConnectionState {
  /** The caller has no linked GitHub account. Fixed by `/auth/github`. */
  notLinked: boolean;
  /** The caller has an account row but no usable token. Also `/auth/github`. */
  tokenMissing: boolean;
  /** Either of the above: any state the user can resolve themselves. */
  needsReconnect: boolean;
  /** The org has no GitHub App installation. An admin action, not self-serve. */
  needsApp: boolean;
}

/**
 * Classify a {@link useGitHubRepos} result into what the caller is missing.
 *
 * Kept as a pure function over an already-fetched result rather than a hook, so
 * a picker and the form around it can share one rule (and one query) instead of
 * each re-deriving it — and drifting. `notLinked` and `tokenMissing` stay
 * separate because they select different copy ("Connect" vs "Reconnect").
 *
 * The two scopes report a missing credential differently: in `user` scope the
 * request fails with an error code, while in `installation` scope it succeeds
 * (the repos come from the org App) and reports the caller's own OAuth state
 * via `hasToken`. Both are folded in here.
 */
export function deriveGitHubConnectionState(
  result: { error: unknown; hasToken?: boolean; hasInstallation?: boolean },
  scope: 'user' | 'installation' = 'user',
): GitHubConnectionState {
  const notLinked =
    isGitHubNotLinkedError(result.error) || (scope === 'installation' && result.hasToken === false);
  const tokenMissing = isGitHubTokenMissingError(result.error);
  return {
    notLinked,
    tokenMissing,
    needsReconnect: notLinked || tokenMissing,
    needsApp: scope === 'installation' && result.hasInstallation === false,
  };
}

/**
 * Repo search for every picker in the app (`@repo:` / `@repository:` mentions,
 * `RepoSelector`, the default-branch lookup below).
 *
 * The search runs in the BROWSER, not on the server. One query per
 * (org, scope) fetches the caller's whole accessible repo list, and every
 * keystroke filters that cached array — so opening a picker costs one request
 * the first time and typing costs none, instead of a round trip per prefix
 * behind a 300ms debounce. Every caller shares the one cache entry, so a
 * picker that mounts after any other has already warmed the list renders from
 * memory.
 *
 * Rows come back RANKED, not in source order: `rankRepos` scores each match on
 * the caller's own pick history (recency, frequency, all-time popularity) plus
 * how recently the repo itself was updated and how well it matches what was
 * typed. Ordering
 * lives here rather than in each picker so `RepoSelector`, the `@repo:` mention
 * menu, and the `@repository:` context menu all agree — and so the mention
 * menus' `slice(0, limit)` keeps the best rows instead of an arbitrary prefix.
 *
 * The server can only answer partially when the caller's account is bigger
 * than {@link REPO_LIST_LIMIT}, or while it is still paging the account out of
 * GitHub. It says so with `complete: false`, and only then does this fall back
 * to asking the server per query (debounced), merging those hits under the
 * ones the partial list already matched.
 */
export function useGitHubRepos(
  query: string,
  enabled = true,
  scope: 'user' | 'installation' = 'user',
) {
  const orgKey = useOrgKey();

  // Cache the full response (not just `repos`) so `hasInstallation` /
  // `hasToken` are available to callers that need to distinguish "search
  // matched nothing" from "there's nothing to search" (e.g. no GitHub App
  // installed for this org at all).
  //
  // The org segment keeps cache slots per-tenant: the server resolves these
  // requests against the caller's *active org* (installation scope) or the
  // token linked while that org was active (user scope). The app-level
  // QueryClient survives an org switch, so without the segment the previous
  // org's repo list is served from cache in the new org — a cross-tenant
  // leak in the picker. The key carries no query text: that is what makes one
  // entry serve every search.
  const list = useQuery<GitHubRepoListResponse>({
    queryKey: ['github-repos', orgKey, scope],
    queryFn: () => githubApi.listGitHubRepos(scope, REPO_LIST_LIMIT),
    enabled,
    staleTime: REPO_STALE_TIME,
    gcTime: REPO_GC_TIME,
    refetchOnWindowFocus: false,
    refetchInterval: (q) =>
      q.state.data && q.state.data.complete === false ? INCOMPLETE_LIST_REFETCH_MS : false,
    retry: (failureCount, error) => {
      if (isGitHubReconnectableError(error)) return false;
      return failureCount < 3;
    },
  });

  const normalized = normalizeRepoQuery(query);
  const isComplete = list.data?.complete === true;
  const debounced = useDebouncedValue(normalized, REPO_SEARCH_DEBOUNCE_MS);
  // Only a partial list needs the server, and only for a non-empty query (an
  // empty one is answered by the list itself).
  const searchEnabled = enabled && !!list.data && !isComplete && debounced.length > 0;

  const search = useQuery<GitHubRepoListResponse>({
    queryKey: ['github-repos', orgKey, scope, 'search', debounced],
    queryFn: () => githubApi.searchGitHubRepos(debounced, scope),
    enabled: searchEnabled,
    staleTime: REPO_STALE_TIME,
    gcTime: REPO_GC_TIME,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (isGitHubReconnectableError(error)) return false;
      return failureCount < 3;
    },
  });

  // Ranking lives here rather than in any one picker so every surface backed
  // by this hook (`RepoSelector`, the `@repo:` mention menu, the launch
  // context provider) orders repos the same way.
  const { history } = useRepoSelectionHistory();

  const repos = useMemo(() => {
    if (!list.data) return undefined;
    const local = filterRepos(list.data.repos, normalized);
    const seen = new Set(local.map((repo) => repo.fullName));
    const matches = searchEnabled
      ? [...local, ...(search.data?.repos ?? []).filter((repo) => !seen.has(repo.fullName))]
      : local;
    return rankRepos(matches, { history, query: normalized });
  }, [list.data, normalized, searchEnabled, search.data, history]);

  return {
    ...list,
    data: repos,
    // The fallback search is part of "is this list still settling?" for any
    // caller showing a spinner.
    isFetching: list.isFetching || search.isFetching,
    hasInstallation: list.data?.hasInstallation,
    hasToken: list.data?.hasToken,
    /** `false` while the cached list is a partial view of the account, so the
     * displayed rows may still be missing matches the server holds. */
    isComplete,
  };
}

/**
 * The default branch of a single `owner/name` repo, or `null` while it is
 * unknown (still loading, no match, or the caller has no usable GitHub token).
 *
 * A `git-repo` attachment with no pinned branch is cloned from the repo's
 * default branch at setup time, so surfaces that show an unpinned attachment
 * need the name to say what will actually be checked out. It comes from the
 * repo search rather than the branch list, which carries no default-branch
 * marker and can be thousands of entries.
 *
 * Prefer the caller's personal OAuth view, but fall back to the org GitHub App
 * when that credential is unlinked, missing, or cannot see the exact repo.
 * Connected attachments are cloned through the App, so they can legitimately
 * reference a private repo the caller's personal credential cannot list.
 *
 * Pass `undefined` to disable the lookup.
 */
export function useGitHubRepoDefaultBranch(repoSlug: string | undefined): string | null {
  const slug = repoSlug?.trim() ?? '';
  const enabled = slug.includes('/');
  // A slug without an owner can't match a `fullName`, so there is nothing worth
  // asking the API for.
  const userResult = useGitHubRepos(slug, enabled);
  const exactMatch = (repos: GitHubRepo[] | undefined) =>
    repos?.find((repo) => repo.fullName.toLowerCase() === slug.toLowerCase());
  const userMatch = exactMatch(userResult.data);
  // Only fall back once the user-scope answer is settled AND authoritative: a
  // list the server flagged incomplete is still filling in, so "no match yet"
  // is not yet "no match".
  const userSettledWithoutMatch =
    enabled && userResult.isSuccess && userResult.isComplete && !userMatch;
  const useInstallation = isGitHubReconnectableError(userResult.error) || userSettledWithoutMatch;
  const installationResult = useGitHubRepos(slug, enabled && useInstallation, 'installation');
  const { data: repos } = useInstallation ? installationResult : userResult;
  // The list is a substring search, so it holds neighbours of the repo we asked
  // about — only an exact slug match answers the question. GitHub owner and
  // repo names are case-insensitive, so compare that way.
  const match = exactMatch(repos);
  return match?.defaultBranch ?? null;
}

/**
 * Resolve a pasted GitHub PR link to its repo slug + head branch. Exposed as a
 * mutation so callers can fire it imperatively (e.g. on paste in the composer).
 */
export function useResolveGitHubPr() {
  return useMutation({
    mutationFn: ({ owner, repo, number }: { owner: string; repo: string; number: number }) =>
      githubApi.resolvePullRequest(owner, repo, number),
  });
}

/** A parsed GitHub pull-request reference. */
export interface PullRequestRef {
  owner: string;
  repo: string;
  number: number;
}

/** Live facts about a pull request, resolved from GitHub. */
export interface PullRequestDetails {
  title: string;
  /** GitHub's raw state; `merged` distinguishes a merged PR from a plain close. */
  state: 'open' | 'closed';
  merged: boolean;
  draft: boolean;
  headBranch: string;
  baseBranch: string;
  /**
   * GitHub's `updated_at` for this row, epoch ms — the version the chip uses
   * to keep the newer of a fetch, a webhook push, and the stored state. `null`
   * from a source that carries none (a push from an older server).
   */
  updatedAt: number | null;
}

/**
 * Fetch the live details of a linked pull request so the PR chip can reflect
 * its real state (a merged/closed PR should stop reading as open) and fill its
 * hover card with the branch flow. Disabled when there is no PR reference or the
 * caller passes `enabled: false` (used to defer the fetch until the card opens).
 * Cached for a few minutes since a PR's facts rarely change, and a reconnectable
 * GitHub error (unlinked account / missing token) isn't retried — the chip just
 * keeps whatever state it already has.
 *
 * `opts.meta` is forwarded to the underlying query so a host can tag the fetch
 * (e.g. to opt it into a global progress indicator); this hook stays
 * unaware of any web-app concept and just passes it through.
 */
export function usePullRequestDetails(
  pr: PullRequestRef | null | undefined,
  opts?: { enabled?: boolean; meta?: Record<string, unknown> },
) {
  const orgKey = useOrgKey();
  const queryClient = useQueryClient();
  const batch = useContext(PullRequestBatchContext);
  // When a batch provider is in scope and is tracking this PR (an `open` PR on
  // the eager surface), the chip reads its details + loading state from the
  // batch and its own single-PR query is held off — the batch is the only
  // fetch, so a focused agent page with N PR chips issues one `pulls/batch`
  // request instead of N `pulls/:number` ones (the Sentry AGENT-FLOW-WEB-4F
  // N+1). A settled-but-missing PR un-tracks so the chip falls back to its own
  // fetch; out-of-scope PRs (merged/closed chips, list rows) ignore the batch.
  const isTrackedByBatch = !!batch && !!pr && (opts?.enabled ?? true) && batch.isTracking(pr);

  // Keep the details live off the server's `pr_updated` push (emitted from the
  // `pull_request` webhook receiver). Writing the pushed row straight into the
  // cache means an open PR flips to merged / closed / draft the instant GitHub
  // delivers the webhook, rather than waiting on the 5-minute stale window or a
  // hover-triggered refetch. Broadcasts are already org-scoped server-side, so
  // every event here belongs to the active org; we still match owner/repo/number
  // so a chip only updates for the PR it is actually showing.
  usePluginEvent(GITHUB_PLUGIN_NAME, GITHUB_PR_UPDATED_EVENT, (payload) => {
    const parsed = GitHubPrUpdatedSchema.safeParse(payload);
    if (!parsed.success) return;
    const d = parsed.data;
    // GitHub owner/repo are case-insensitive: the server sends canonical casing
    // (`owner.login` / `repo.name`) while `pr` is parsed from the stored PR URL,
    // which may differ in case. Match case-insensitively so a non-canonically
    // cased URL doesn't silently miss its live update. The cache key still uses
    // the URL's casing so it lands on the same slot the query reads.
    if (
      !pr ||
      d.owner.toLowerCase() !== pr.owner.toLowerCase() ||
      d.repo.toLowerCase() !== pr.repo.toLowerCase() ||
      d.number !== pr.number
    ) {
      return;
    }
    const details: PullRequestDetails = {
      title: d.title,
      state: d.state,
      merged: d.merged,
      draft: d.draft,
      headBranch: d.headBranch,
      baseBranch: d.baseBranch,
      updatedAt: d.updatedAt ?? null,
    };
    // Keep whichever row is newer: GitHub delivers webhooks out of order, so a
    // push can carry an older snapshot than the one already cached.
    queryClient.setQueryData<PullRequestDetails>(
      ['github-pr-details', orgKey, pr.owner, pr.repo, pr.number],
      (current) => (isNewerPullRequestDetails(details, current) ? details : current),
    );
    // When the chip is reading from the batch context, the per-PR cache write
    // above isn't observed (its query is held off), so route the live update
    // through the batch overlay instead — it beats the (possibly pre-push)
    // batch result so a merged PR flips without a refetch.
    batch?.reportUpdate(pr, details);
  });

  const result = useQuery<PullRequestDetails>({
    meta: opts?.meta,
    // Org segment mirrors the other GitHub hooks: PR details are resolved with
    // the caller's org-scoped token, so a keyless slot could leak across tenants.
    queryKey: ['github-pr-details', orgKey, pr?.owner, pr?.repo, pr?.number],
    queryFn: async () => {
      if (!pr) throw new Error('missing pull request reference');
      const d = await githubApi.resolvePullRequest(pr.owner, pr.repo, pr.number);
      return {
        title: d.title,
        state: d.state,
        merged: d.merged,
        draft: d.draft,
        headBranch: d.headBranch,
        baseBranch: d.baseBranch,
        updatedAt: d.updatedAt,
      };
    },
    // Hold the per-PR query off while the batch is fetching this PR — the batch
    // is the single fetch. Once the batch settles it has either returned this
    // PR's details (the chip reads them via the context, so the query stays off
    // and never refetches) or missed it (`isTracking` flips false, the query
    // enables, and the chip falls back to a single-PR fetch).
    enabled: !!pr && (opts?.enabled ?? true) && !isTrackedByBatch,
    staleTime: REPO_STALE_TIME,
    gcTime: REPO_GC_TIME,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (isGitHubReconnectableError(error)) return false;
      return failureCount < 2;
    },
  });

  // When tracked by the batch, surface the batch's details + its in-flight
  // loading state (so the chip keeps its greyed "verifying" look through the
  // batch window) in the same `UseQueryResult` shape the chip already reads.
  if (isTrackedByBatch) {
    return {
      ...result,
      data: batch!.detailsFor(pr!),
      isLoading: batch!.isFetching(pr!),
    };
  }
  return result;
}

export function useGitHubBranches(
  owner: string | null,
  repo: string | null,
  scope: 'user' | 'installation' = 'user',
) {
  const orgKey = useOrgKey();
  // Org segment for the same reason as `useGitHubRepos`: branch lists are
  // fetched with per-org installation tokens, so a cache slot without the
  // org would serve one tenant's private branch names to another.
  return useQuery<GitHubBranch[]>({
    queryKey: ['github-branches', orgKey, scope, owner, repo],
    queryFn: async () => {
      if (!owner || !repo) return [];
      const res = await githubApi.getGitHubBranches(owner, repo, scope);
      return res.branches;
    },
    enabled: !!owner && !!repo,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });
}
